import { positionalIdsFor } from '../store/positionalCfIds';
import { cfHashId } from '../store/cfHash';
import type { CanvasDoc, Frame, MotionTimeline, NoteTarget } from '../store/types';
import { extractSlotSchemas, injectCfNodeIds } from '../store/slot-schema';
import { parseFragment, serializeFragment } from '../store/parseFragment';

/** old-positional id -> new content-hash id, for one html frame. */
export type IdMap = Record<string, string>;

const LEGACY_POSITIONAL = /^cf-\d+$/;

/**
 * Build old->new for a frame's html. Parses once, reads the id each element WOULD
 * have had positionally (existing or freshly counted), and its new id, zipped
 * element-by-element. Legacy positional ids (/^cf-\d+$/) and never-persisted
 * nodes map to a content-hash; author/already-hashed ids map to themselves.
 */
export function buildFrameIdMap(html: string): IdMap {
  const { root, ids: oldIds } = positionalIdsFor(html);
  const els = Array.from(root.querySelectorAll('*'));
  const map: IdMap = {};
  els.forEach((el, i) => {
    const oldId = oldIds[i];
    const existing = el.getAttribute('data-cf-id');
    if (existing && !LEGACY_POSITIONAL.test(existing)) {
      map[oldId] = existing; // author / already-hashed → identity
    } else {
      map[oldId] = cfHashId(el); // legacy positional OR never-persisted → hash
    }
  });
  return map;
}

/**
 * Rewrite a frame's html so legacy positional ids become content-hash, keeping the
 * old positional id as data-cf-alias (dual-id, one release). Author/already-hashed
 * ids are preserved (no alias); never-persisted nodes stay id-less.
 */
export function rewriteFrameHtml(html: string): string {
  const host = parseFragment(html);
  for (const el of Array.from(host.querySelectorAll('*'))) {
    const oldId = el.getAttribute('data-cf-id');
    if (!oldId) continue; // never-persisted — leave id-less
    if (!LEGACY_POSITIONAL.test(oldId)) continue; // author/hashed — preserve, no alias
    el.setAttribute('data-cf-id', cfHashId(el));
    el.setAttribute('data-cf-alias', oldId);
  }
  return serializeFragment(host);
}

/** Frames whose html the migration hashes: free + main (own html + persist cf-ids).
 *  instance resolves a main's html; tsx persists no html — neither is rewritten. */
function ownsHtml(f: Frame): f is Extract<Frame, { kind: 'free' | 'main' }> {
  return f.kind === 'free' || f.kind === 'main';
}

/** Remap an element NoteTarget's nodeId through its frame's map. Non-element
 *  targets, targets to non-html / absent frames, and unmapped ids pass through
 *  unchanged (stale dangling refs are tolerated — they resolve to null at runtime
 *  exactly as before, since no node exists to point at). */
function remapTarget(t: NoteTarget, maps: Map<string, IdMap>): NoteTarget {
  if (t.kind !== 'element') return t;
  const m = maps.get(t.frameId);
  if (!m) return t;
  const next = m[t.nodeId];
  return next && next !== t.nodeId ? { ...t, nodeId: next } : t;
}

function remapOptionalTarget(
  t: NoteTarget | undefined,
  maps: Map<string, IdMap>,
): NoteTarget | undefined {
  return t ? remapTarget(t, maps) : t;
}

/** Remap every cf-id a frame's motion timeline references through its id map:
 *  track targets, trigger targets, and a cf-id scroll source. Unmapped ids pass
 *  through (alias fallback covers them for the dual-id window). */
function remapMotion(motion: MotionTimeline | undefined, m: IdMap): MotionTimeline | undefined {
  if (!motion) return motion;
  const playback = { ...motion.playback };
  if (playback.triggers) {
    playback.triggers = playback.triggers.map((tg) =>
      tg.target ? { ...tg, target: m[tg.target] ?? tg.target } : tg,
    );
  }
  if (playback.scroll && playback.scroll.source !== 'frameInView') {
    playback.scroll = {
      ...playback.scroll,
      source: m[playback.scroll.source] ?? playback.scroll.source,
    };
  }
  return {
    ...motion,
    tracks: motion.tracks.map((tr) => ({ ...tr, target: m[tr.target] ?? tr.target })),
    playback,
  };
}

/**
 * Load-time content-hash migration. Idempotent (returns the SAME doc reference if
 * cfIdScheme==='hash'). For every html-owning frame: rewrite html (dual-id alias),
 * remap motion track targets, recompute main slots. Globally remap comment + note
 * element targets. TSX frames are NOT touched (positional ids, deferred).
 */
export function migrateCfIds(doc: CanvasDoc): CanvasDoc {
  if (doc.cfIdScheme === 'hash') return doc;
  const maps = new Map<string, IdMap>();
  for (const f of doc.frames) {
    if (ownsHtml(f)) maps.set(f.id, buildFrameIdMap(f.html));
  }

  const frames = doc.frames.map((f): Frame => {
    if (ownsHtml(f)) {
      const m = maps.get(f.id)!;
      const motion = remapMotion(f.motion, m);
      if (f.kind === 'main') {
        // Rewrite positional->hash+alias, then stamp bare slot elements so every
        // slot has a resolvable hash id (seed mains have data-slot without data-cf-id).
        const html = injectCfNodeIds(rewriteFrameHtml(f.html));
        return { ...f, html, motion, slots: extractSlotSchemas(html) };
      }
      return { ...f, html: rewriteFrameHtml(f.html), motion };
    }
    if (f.kind === 'note') {
      return { ...f, target: remapOptionalTarget(f.target, maps) };
    }
    return f;
  });

  const comments = doc.comments?.map((c) => ({ ...c, target: remapTarget(c.target, maps) }));

  return { ...doc, frames, ...(comments ? { comments } : {}), cfIdScheme: 'hash' };
}
